import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiRefreshCw } = FiIcons;

function ScienceGame() {
  const navigate = useNavigate();
  const { state, dispatch } = useGame();
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [questionsCompleted, setQuestionsCompleted] = useState(0);
  const [gameCompleted, setGameCompleted] = useState(false);

  const scienceQuestions = {
    beginner: [
      {
        question: "What do plants need to grow?",
        answers: ["Sunlight and water", "Only darkness", "Only rocks", "Only air"],
        correct: 0,
        emoji: "🌱",
        explanation: "Plants need sunlight and water to make their own food!"
      },
      {
        question: "Which animal lives in water?",
        answers: ["Lion", "Fish", "Bird", "Dog"],
        correct: 1,
        emoji: "🐠",
        explanation: "Fish live in water and breathe through their gills!"
      },
      {
        question: "What happens when you mix red and yellow?",
        answers: ["Blue", "Orange", "Green", "Purple"],
        correct: 1,
        emoji: "🎨",
        explanation: "Red and yellow make orange!"
      },
      {
        question: "Which one is the hottest?",
        answers: ["Ice", "Snow", "Sun", "Moon"],
        correct: 2,
        emoji: "☀️",
        explanation: "The Sun is very hot and gives us light and warmth!"
      },
      {
        question: "What do bees make?",
        answers: ["Milk", "Honey", "Eggs", "Cheese"],
        correct: 1,
        emoji: "🐝",
        explanation: "Bees make honey from flower nectar!"
      }
    ],
    intermediate: [
      {
        question: "What are the three states of water?",
        answers: ["Hot, cold, warm", "Solid, liquid, gas", "Big, small, tiny", "Fast, slow, still"],
        correct: 1,
        emoji: "💧",
        explanation: "Water can be ice (solid), water (liquid), or steam (gas)!"
      },
      {
        question: "Which planet is closest to the Sun?",
        answers: ["Earth", "Mars", "Mercury", "Venus"],
        correct: 2,
        emoji: "🪐",
        explanation: "Mercury is the closest planet to the Sun!"
      },
      {
        question: "What do we call baby frogs?",
        answers: ["Puppies", "Tadpoles", "Kittens", "Chicks"],
        correct: 1,
        emoji: "🐸",
        explanation: "Baby frogs are called tadpoles!"
      },
      {
        question: "What gas do plants give off?",
        answers: ["Carbon dioxide", "Oxygen", "Nitrogen", "Helium"],
        correct: 1,
        emoji: "🌿",
        explanation: "Plants give off oxygen that we breathe!"
      },
      {
        question: "What causes thunder?",
        answers: ["Rain", "Wind", "Lightning", "Clouds"],
        correct: 2,
        emoji: "⚡",
        explanation: "Lightning causes thunder by heating the air very quickly!"
      }
    ],
    advanced: [
      {
        question: "What is the process plants use to make food?",
        answers: ["Respiration", "Photosynthesis", "Digestion", "Circulation"],
        correct: 1,
        emoji: "🌳",
        explanation: "Photosynthesis is how plants use sunlight to make food!"
      },
      {
        question: "What force pulls objects toward Earth?",
        answers: ["Magnetism", "Electricity", "Gravity", "Friction"],
        correct: 2,
        emoji: "🍎",
        explanation: "Gravity pulls everything toward the center of Earth!"
      },
      {
        question: "What do we call animals that eat only plants?",
        answers: ["Carnivores", "Herbivores", "Omnivores", "Predators"],
        correct: 1,
        emoji: "🦌",
        explanation: "Herbivores are animals that eat only plants!"
      },
      {
        question: "How many bones are in an adult human body?",
        answers: ["106", "206", "306", "406"],
        correct: 1,
        emoji: "🦴",
        explanation: "An adult human has 206 bones!"
      },
      {
        question: "What is the hardest natural substance?",
        answers: ["Gold", "Iron", "Diamond", "Silver"],
        correct: 2,
        emoji: "💎",
        explanation: "Diamond is the hardest natural substance on Earth!"
      }
    ]
  };

  const getQuestionSet = () => {
    if (state.age <= 7) return scienceQuestions.beginner;
    if (state.age <= 9) return scienceQuestions.intermediate;
    return scienceQuestions.advanced;
  };

  const generateQuestion = () => {
    const questionSet = getQuestionSet();
    return questionSet[Math.floor(Math.random() * questionSet.length)];
  };

  useEffect(() => {
    setCurrentQuestion(generateQuestion());
  }, [state.age]);

  const handleAnswerSelect = (answerIndex) => {
    setSelectedAnswer(answerIndex);
    setShowResult(true);
    
    const isCorrect = answerIndex === currentQuestion.correct;
    
    if (isCorrect) {
      dispatch({ type: 'ADD_SCORE', payload: 20 });
    } else {
      dispatch({ type: 'ADD_WRONG_ANSWER' });
    }
    
    setTimeout(() => {
      const newCompleted = questionsCompleted + 1;
      setQuestionsCompleted(newCompleted);
      
      if (newCompleted >= 5) {
        setGameCompleted(true);
        dispatch({ type: 'GAME_COMPLETED' });
      } else {
        setCurrentQuestion(generateQuestion());
        setSelectedAnswer(null);
        setShowResult(false);
      }
    }, 3000);
  };

  const restartGame = () => {
    setCurrentQuestion(generateQuestion());
    setSelectedAnswer(null);
    setShowResult(false);
    setQuestionsCompleted(0);
    setGameCompleted(false);
  };

  if (!currentQuestion && !gameCompleted) {
    return <div>Loading...</div>;
  }

  if (gameCompleted) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen flex items-center justify-center p-4"
      >
        <div className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-safari-200 text-center max-w-md">
          <div className="text-6xl mb-4">🔬</div>
          <h2 className="text-3xl font-kid text-jungle-700 mb-4">
            Science Star!
          </h2>
          <p className="font-comic text-jungle-600 mb-6">
            You completed the Science Lab! You're becoming a great scientist!
          </p>
          
          <div className="space-y-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={restartGame}
              className="w-full bg-gradient-to-r from-teal-500 to-teal-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Play Again
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gradient-to-r from-safari-500 to-safari-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Back to Games
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen p-4"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex items-center justify-between mb-8"
        >
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
          >
            <SafeIcon icon={FiArrowLeft} className="text-jungle-600" />
            <span className="font-comic text-jungle-700">Back</span>
          </button>

          <h1 className="text-3xl font-kid text-jungle-700">Science Lab 🔬</h1>

          <button
            onClick={restartGame}
            className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
          >
            <SafeIcon icon={FiRefreshCw} className="text-jungle-600" />
            <span className="font-comic text-jungle-700">Restart</span>
          </button>
        </motion.div>

        {/* Progress */}
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${(questionsCompleted / 5) * 100}%` }}
          className="h-3 bg-teal-400 rounded-full mb-8 shadow-inner"
        >
          <div className="w-full h-full bg-gradient-to-r from-teal-400 to-teal-500 rounded-full"></div>
        </motion.div>

        {/* Question */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl p-8 shadow-xl border-4 border-safari-200 mb-8"
        >
          {/* Question Display */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              className="text-6xl mb-4"
            >
              {currentQuestion.emoji}
            </motion.div>
            <h2 className="text-2xl font-kid text-jungle-700 mb-4">
              {currentQuestion.question}
            </h2>
          </div>

          {/* Answer Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {currentQuestion.answers.map((answer, index) => (
              <motion.button
                key={index}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => !showResult && handleAnswerSelect(index)}
                disabled={showResult}
                className={`p-6 rounded-2xl font-comic text-lg shadow-lg transition-all duration-200 ${
                  showResult
                    ? index === currentQuestion.correct
                      ? 'bg-green-400 text-white'
                      : index === selectedAnswer
                      ? 'bg-red-400 text-white'
                      : 'bg-gray-200 text-gray-500'
                    : 'bg-gradient-to-br from-safari-200 to-safari-300 text-jungle-700 hover:from-safari-300 hover:to-safari-400'
                }`}
              >
                {answer}
              </motion.button>
            ))}
          </div>

          {/* Result Message */}
          <AnimatePresence>
            {showResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mt-6 text-center"
              >
                {selectedAnswer === currentQuestion.correct ? (
                  <div className="text-green-600 font-kid text-xl mb-2">
                    🎉 Excellent! You're right!
                  </div>
                ) : (
                  <div className="text-red-600 font-kid text-xl mb-2">
                    Good try! The correct answer is: {currentQuestion.answers[currentQuestion.correct]}
                  </div>
                )}
                <p className="font-comic text-jungle-600 text-base">
                  {currentQuestion.explanation}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default ScienceGame;